import {
    SCREEN_CHANGE,
    JUMP_SYSTEM,
    JUMP_SYSTEM_START,
    JUMP_SYSTEM_FAILED,
    LOGIN_SUCCESS,
    LOGIN_RESULT,
    SET_AUTH_SERVERS,
    SET_ALL_CAMERAS,
    SET_USER_DATA,
    SET_USER_RIGHTS,
    SET_USER_CAMERAS,
    SET_AUTOSCAN_TIMEOUT,
    SET_PTZ_CONFIG,
    SET_PTZ_PRESETS,
    SET_ACTIVE_SERVER,
    GET_SERVER,
    SET_VIEWS,
    SET_CURRENT_VIEW,
    RESTORE_ORIGINAL_CAMERAS,
    SET_EVIEW_LOGIN,
    SET_ALL_EVIEW_CAMERAS,
    SET_SERVER_SESSIONS,
    SET_JUMP_ERROR,
    SET_ACCESS_NOTICE,
    CAM_VIEW_CHANGED
  } from './types';
import axios from 'axios';
import history from '../history';
import { getApiUrl, setJumpedServerUrl, devProxyUrl, buildOrderedMemberUrls } from '../util/getServerInfo';
import { getClientVantageHost } from '../util/serverStatus';
import { clearStreamPrefs } from '../util/clearStreamPrefs';
import { getViews as getViewsAction, autoSelectDefaultView } from './ViewActions';
import { getCamViewForConf } from '../reducers/CamButtonsReducer';
import { debugLog, debugWarn } from '../util/debug';
import { selectServerValues } from '../util/serverRights';

export const screenChange = screen => {
  return {
    type: SCREEN_CHANGE,
    payload: screen
  };
};

export const clearAccessNotice = () => ({ type: SET_ACCESS_NOTICE, payload: '' });

export const clearJumpError = () => {
  return { type: SET_JUMP_ERROR, payload: '' };
};

/**
 * Jump System - Connect to a different NVR server
 *
 * This mirrors the Java dview's AuthHelper.whichIP() logic:
 * 1. Try public IP first (if previously successful or preferred)
 * 2. Fall back to local IP if public fails
 * 3. Try local IP with non-standard port if needed
 * 4. Final fallback to public IP
 *
 * Note: Browser requires CORS headers on target servers for cross-origin requests.
 */

// Track which IP type worked (persists to localStorage across jumps like Java's fUsePublic)
// Note: Variable tracks state for debugging; actual persistence is via localStorage

/**
 * Try to reach a server and verify its serial number
 * Mirrors Java's whichIP verification logic
 */
const tryServerConnection = async (url, expectedSerial, timeout = 3000) => {
  try {
    debugLog(`[jumpSystem] Trying: ${url}`);
    const resp = await axios.post(devProxyUrl(url), {
      jsonrpc: 2.0,
      method: 'info.getSerial',
      id: 1
    }, { timeout });

    const actualSerial = resp.data?.result?.[1];
    if (actualSerial === expectedSerial) {
      debugLog(`[jumpSystem] ✓ Success: ${url} (serial ${actualSerial})`);
      return true;
    }
    debugLog(`[jumpSystem] ✗ Serial mismatch: expected ${expectedSerial}, got ${actualSerial}`);
    return false;
  } catch (e) {
    debugLog(`[jumpSystem] ✗ Failed: ${url} - ${e.message}`);
    return false;
  }
};

/**
 * Per-bSerial cache for resolved server URLs. Mirrors Java's
 * SetModel.rghSessionAddress map — once we know which of a server's
 * candidate URLs reaches it, reuse that decision until TTL expiry or
 * a verify failure. Cuts jump latency from ~6s (off-LAN sequential
 * timeout sweep) to ~100ms (cache verify) on every repeat jump.
 *
 * 2 hour TTL is short enough to re-discover when the user moves
 * between networks (office LAN ↔ remote) but long enough that the
 * 134-server fan-out in fetchAllServerCamerasForJump doesn't re-probe
 * on every jump.
 */
const WHICH_IP_CACHE_TTL_MS = 2 * 60 * 60 * 1000;
const cacheKeyFor = (bSerial) => `whichIP_${bSerial}`;

const getCachedWhichIP = (bSerial) => {
  try {
    const raw = localStorage.getItem(cacheKeyFor(bSerial));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || !entry.url || !entry.ts) return null;
    if (Date.now() - entry.ts > WHICH_IP_CACHE_TTL_MS) {
      localStorage.removeItem(cacheKeyFor(bSerial));
      return null;
    }
    return { ip: entry.ip, port: entry.port, url: entry.url };
  } catch (e) {
    return null;
  }
};

const setCachedWhichIP = (bSerial, ip, port, url) => {
  try {
    localStorage.setItem(cacheKeyFor(bSerial), JSON.stringify({ ip, port, url, ts: Date.now() }));
  } catch (e) {
    // Quota exceeded — ignore. Cache is a perf optimization, not required.
  }
};

const clearCachedWhichIP = (bSerial) => {
  try { localStorage.removeItem(cacheKeyFor(bSerial)); } catch (e) {}
};

/**
 * Guess-then-failover for a member's eView stream URL (#86bb7phhh).
 *
 * The login/jump paths GUESS each member's URL synchronously (vantage-ordered, no probe) so login is
 * instant. If that guess is wrong — classically NAT hairpin: we picked the member's PUBLIC IP but from
 * here only its LAN IP answers (or vice-versa) — the member's JPEG base errors and the tile would sit on
 * "failed" forever even though the member IS reachable on its OTHER IP. CameraStreamContainer's image
 * onError fires this for the failing member: probe the active URL, and if it's genuinely unreachable,
 * fail over to the stored alternate (serverUrlAlt). Whichever verifies (serial-checked) becomes the
 * member's serverUrl, and every tile for that member reconnects on it. If NEITHER answers, mark the
 * member urlUnreachable — a real "can't connect", not a wrong guess.
 *
 * Idempotent per member: a synchronous in-flight guard + redux urlResolving/urlResolved/urlUnreachable
 * flags stop the ~5s JPEG-error retries from re-probing. serverSessions is rebuilt on each login/jump,
 * which clears the flags and re-enables one resolution pass.
 */
const resolvingMembers = new Set();

export const resolveMemberServerUrl = (bSerial) => async (dispatch, getState) => {
  if (!bSerial) return;
  if (resolvingMembers.has(bSerial)) return;   // guard the JPEG-error storm (fires every ~5s)

  const sessions = (getState().server && getState().server.serverSessions) || {};
  const member = sessions[bSerial];
  if (!member) return;
  if (member.urlResolved || member.urlUnreachable) return;   // already settled this session

  const active = member.serverUrl;
  // Walk EVERY stored candidate. serverUrlAlts is the full vantage-ordered list; fall back to the
  // singular serverUrlAlt for sessions built before that field existed. #86bb7phhh
  const alts = (member.serverUrlAlts && member.serverUrlAlts.length)
    ? member.serverUrlAlts
    : [member.serverUrlAlt].filter(Boolean);
  if (!active && !alts.length) return;   // nothing to probe or fail over to

  resolvingMembers.add(bSerial);   // synchronous — set BEFORE any await so concurrent tiles bail here

  // Apply the settled result onto the CURRENT member — but only if it hasn't changed out from under us.
  // A login/jump completing mid-probe dispatches a full-map SET_SERVER_SESSIONS that replaces this entry;
  // we must NOT resurrect our start-of-thunk snapshot or clobber the fresh one. If serverUrl no longer
  // matches the URL we probed, drop the result — the fresh member resolves on its own. #86bb7phhh
  const settle = (patch) => {
    const cur = ((getState().server && getState().server.serverSessions) || {})[bSerial];
    if (!cur || cur.serverUrl !== active) return;
    dispatch({ type: SET_SERVER_SESSIONS, payload: { [bSerial]: { ...cur, ...patch } } });
  };

  try {
    // 1) Is the active guess actually reachable? A transient JPEG blip re-verifies fine → keep it.
    if (active && await tryServerConnection(active, bSerial, 2500)) {
      settle({ urlResolved: true });
      return;
    }
    // 2) Active is unreachable — try each alternate in vantage order (the whole point).
    for (let i = 0; i < alts.length; i += 1) {
      const alt = alts[i];
      // eslint-disable-next-line no-await-in-loop
      if (alt && await tryServerConnection(alt, bSerial, 2500)) {
        debugLog(`[resolveMemberServerUrl] ${bSerial}: active unreachable, failing over → ${alt}`);
        // The one that answered becomes active; everything else (including the old active) stays a
        // candidate, so a later move can fail over again.
        const rest = [active].concat(alts.filter((u) => u !== alt)).filter(Boolean);
        settle({ serverUrl: alt, serverUrlAlt: rest[0] || null, serverUrlAlts: rest, urlResolved: true });
        return;
      }
    }
    // 3) No candidate answers — genuinely unreachable.
    debugWarn(`[resolveMemberServerUrl] ${bSerial}: no reachable URL (active=${active}, alts=${alts.join(',')})`);
    settle({ urlUnreachable: true });
  } catch (e) {
    // leave unsettled so a later error can re-trigger
  } finally {
    resolvingMembers.delete(bSerial);
  }
};

/**
 * Determine which IP to use for connecting to a server.
 * Ports Java AuthHelper.whichIPParallel (dview/src/.../AuthHelper.java:391-441):
 * race every candidate URL in parallel, first matching-serial response wins,
 * with a shared 5s deadline across the whole race. Result is cached per
 * bSerial so subsequent jumps skip the probe entirely (matches Java's
 * SetModel.rghSessionAddress reuse pattern).
 *
 * Previous behavior was sequential local → local:port → public with 3+3+5s
 * timeouts. Off-LAN that always wasted ~6s per jump on dead local attempts
 * before falling through to public. The parallel race finishes in <1s
 * whenever any candidate is reachable. ClickUp 86baduzfv.
 */
const whichIP = async (server) => {
  const { sIP, sLocalIP, bPort, bSerial } = server;
  const portSuffix = bPort && bPort !== 80 ? `:${bPort}` : '';

  // Step 1: Cache fast path. Re-verify with a short timeout — if the cached
  // URL still answers, skip the full race. ~100ms when the cache is valid.
  const cached = getCachedWhichIP(bSerial);
  if (cached) {
    if (await tryServerConnection(cached.url, bSerial, 1500)) {
      if (process.env.NODE_ENV === 'development') {
        debugLog(`[jumpSystem] whichIP cache hit for ${bSerial}: ${cached.url}`);
      }
      return cached;
    }
    // Cached URL no longer works (network change, server moved, etc.) —
    // drop it and fall through to a fresh parallel race below.
    clearCachedWhichIP(bSerial);
    if (process.env.NODE_ENV === 'development') {
      debugLog(`[jumpSystem] whichIP cache stale for ${bSerial}, re-probing`);
    }
  }

  // Step 2: Build the candidate list. All three flavors race together.
  const candidates = [];
  if (sLocalIP) {
    candidates.push({ url: `http://${sLocalIP}/JSON/`, ip: sLocalIP, port: 80 });
    if (bPort && bPort !== 80) {
      candidates.push({ url: `http://${sLocalIP}:${bPort}/JSON/`, ip: sLocalIP, port: bPort });
    }
  }
  if (sIP) {
    candidates.push({ url: `http://${sIP}${portSuffix}/JSON/`, ip: sIP, port: bPort || 80 });
  }
  if (candidates.length === 0) {
    // Server with no IPs at all — return a best-effort fallback. Caller
    // will hit a network error if this isn't reachable.
    const ip = sIP || sLocalIP || '127.0.0.1';
    return { ip, port: bPort || 80, url: `http://${ip}${portSuffix}/JSON/` };
  }

  // Step 3: Race candidates in parallel. First matching-serial response wins,
  // losers continue but their results are ignored. Hard 5s deadline.
  const RACE_DEADLINE_MS = 5000;
  const winner = await new Promise((resolve) => {
    let remaining = candidates.length;
    let settled = false;
    const finish = (val) => {
      if (!settled) { settled = true; resolve(val); }
    };
    candidates.forEach((cand) => {
      tryServerConnection(cand.url, bSerial, RACE_DEADLINE_MS).then((ok) => {
        if (ok) {
          finish(cand);
          return;
        }
        remaining -= 1;
        if (remaining === 0) finish(null);
      }).catch(() => {
        remaining -= 1;
        if (remaining === 0) finish(null);
      });
    });
    // Safety net in case all candidates hang past the deadline.
    setTimeout(() => finish(null), RACE_DEADLINE_MS);
  });

  // Step 4: Cache the winner and return; otherwise fall back to public IP.
  if (winner) {
    setCachedWhichIP(bSerial, winner.ip, winner.port, winner.url);
    // Kept for backward compat with any consumers that read this flag.
    localStorage.setItem('usePublicIp', winner.ip === sIP ? 'true' : 'false');
    if (process.env.NODE_ENV === 'development') {
      debugLog(`[jumpSystem] whichIP race winner: ${winner.url}`);
    }
    return winner;
  }

  // No candidate confirmed serial match within the deadline. Default to the
  // public IP — the subsequent login call will throw a clearer error if it
  // really is unreachable, and CORS-only failures still let images stream.
  if (process.env.NODE_ENV === 'development') {
    debugLog('[jumpSystem] whichIP race had no winner; defaulting to public IP');
  }
  const fallbackIp = sIP || sLocalIP;
  const fallbackUrl = `http://${fallbackIp}${portSuffix}/JSON/`;
  return { ip: fallbackIp, port: bPort || 80, url: fallbackUrl };
};

/**
 * Fetch cameras from all connected servers for eView mode during jump
 *
 * IMPORTANT: This mirrors Java's ControllerCameraModel which calls config.server.getCameras
 * on the TARGET SERVER ONLY. The server aggregates camera data from all member servers
 * via the enterprise sync mechanism (ControllerCamera table).
 *
 * This approach does NOT create sessions on member servers, avoiding orphaned sessions.
 * Video streaming uses user/pass authentication, not sessions.
 */
const fetchAllServerCamerasForJump = async (dispatch, primarySess, username, password, authServers, localSerial, primaryServerUrl) => {
  debugLog('[jumpSystem] fetchAllServerCamerasForJump - eView mode (Java-aligned)');
  debugLog('[jumpSystem] Using config.server.getCameras on target server only');
  debugLog('[jumpSystem] authServers:', authServers?.length, 'localSerial:', localSerial);

  if (!authServers || authServers.length === 0) {
    debugWarn('[jumpSystem] No auth servers available');
    return [];
  }

  const allCameras = [];
  const serverSessions = {};
  const primaryProxyUrl = devProxyUrl(primaryServerUrl);

  // Build a lookup map from authServers for server metadata
  const serverMetadata = {};
  authServers.forEach(server => {
    serverMetadata[server.bSerial] = server;
  });

  // Step 1: Call config.server.getCameras on the TARGET SERVER (after jump)
  // This returns aggregated camera data for ALL servers without needing to login to each
  debugLog('[jumpSystem] Calling config.server.getCameras on target server...');
  let serverCamerasData = [];
  try {
    const camerasResp = await axios.post(primaryProxyUrl, {
      jsonrpc: 2.0,
      method: 'config.server.getCameras',
      id: 101,
      params: [primarySess]
    }, { timeout: 10000 });

    serverCamerasData = camerasResp.data?.result?.[1] || [];
    debugLog(`[jumpSystem] Got camera data for ${serverCamerasData.length} servers from target`);
  } catch (err) {
    console.error('[jumpSystem] Failed to fetch cameras from target server:', err.message);
    return [];
  }

  // Step 2: Get target server's timezone (used as fallback for all servers)
  let targetTimezone = '';
  try {
    const dtimeResp = await axios.post(primaryProxyUrl, {
      jsonrpc: 2.0,
      method: 'config.dtime.getAll',
      id: 102,
      params: [primarySess]
    }, { timeout: 3000 });
    targetTimezone = dtimeResp.data?.result?.[1]?.sTimezone || '';
    debugLog('[jumpSystem] Target server timezone:', targetTimezone);
  } catch (err) {
    debugWarn('[jumpSystem] Failed to fetch target timezone, using fallback');
  }

  // Step 3: Build URLs for each connected NVR — NO per-server probe.
  //
  // Previous code looped every authServer through whichIP, firing 2-3 HTTP
  // probes per server. With 134 connected NVRs that's ~400 requests on
  // every jump, even when the connected-server list and their IPs haven't
  // changed since the last jump. ClickUp 86baduzfv.
  //
  // New strategy (mirrors Java SetModel.rghSessionAddress reuse):
  //   1. If we have a cached whichIP entry for this bSerial, use it.
  //   2. Otherwise default to the server's public IP — works for any
  //      off-LAN viewer (which is the dominant case). Streams that fail
  //      will surface "Connection Failed" on just that camera; the next
  //      ServerView 30-min poll refreshes accessibility status.
  //   3. The first time a stream actually starts to a previously-unprobed
  //      server, the on-target jump's whichIP will probe + cache that one
  //      server (when the user jumps directly to it later).
  //
  // The TARGET controller still gets a verified URL — that probe happens
  // in the main jump thunk at NavigationActions.js whichIP() call, not here.
  const vantageHost = getClientVantageHost();
  authServers.forEach((server) => {
    const { bSerial, sName } = server;

    let serverUrl;
    let serverUrlAlt = null;
    let serverUrlAlts = [];
    if (bSerial === localSerial) {
      // Target controller — already-resolved URL from the main jump.
      serverUrl = primaryServerUrl;
    } else {
      // Remote NVR — vantage-ordered [preferred, alt] guess (no probe), cached result wins as the
      // active URL. serverUrlAlt is the failover resolveMemberServerUrl flips to if the guess is wrong
      // (NAT hairpin), so a jumped-to enterprise self-heals the same way login does. #86bb7phhh
      const urls = buildOrderedMemberUrls(server, vantageHost);
      const cached = getCachedWhichIP(bSerial);
      serverUrl = (cached && cached.url) || urls[0];
      // Every CURRENT candidate that isn't the active one stays as a failover. This used to take only
      // the first, so a STALE cached URL (the member's address changed inside the 2h cache window) both
      // became active AND crowded out all but one live candidate — if that one was unreachable the
      // member was declared dead while a perfectly good URL was never tried. #86bb7phhh
      serverUrlAlts = urls.filter((u) => u !== serverUrl);
      serverUrlAlt = serverUrlAlts[0] || null;
    }

    serverSessions[bSerial] = {
      sSess: bSerial === localSerial ? primarySess : null,
      serverUrl,
      serverUrlAlt,
      serverUrlAlts,
      sName,
      sTimezone: targetTimezone
    };
  });

  // Step 4: Flatten camera data and enrich with server metadata
  serverCamerasData.forEach(serverData => {
    const bSerial = serverData.bSerial;
    const cameras = serverData.rgoCam || [];
    const metadata = serverMetadata[bSerial] || {};
    const sName = metadata.sName || `Server ${bSerial}`;
    const serverUrl = serverSessions[bSerial]?.serverUrl || '';

    cameras.forEach(cam => {
      allCameras.push({
        bID: cam.bID,
        sName: cam.sName,
        fEnable: cam.fEnable || false,
        fPtz: cam.fPtz || false,
        fAudio: cam.fAudio || false,
        fMPE: cam.fMPE || false,
        fFisheye: cam.fFisheye || false,
        bSerial: bSerial,
        sServerName: sName,
        serverUrl: serverUrl,
        eviewDisplayName: `${sName} : ${String(cam.bID).padStart(2, '0')} ${cam.sName}`,
        compositeKey: `nvr_${bSerial}_cam_${cam.bID}`
      });
    });
  });

  debugLog(`[jumpSystem] Total eView cameras: ${allCameras.length} from ${serverCamerasData.length} servers`);
  debugLog('[jumpSystem] NO sessions created on member servers (Java-aligned behavior)');

  // Dispatch aggregated cameras
  dispatch({ type: SET_ALL_EVIEW_CAMERAS, payload: allCameras });

  // Dispatch server info for video streaming
  dispatch({ type: SET_SERVER_SESSIONS, payload: serverSessions });

  return allCameras;
};

export const jumpSystem = (server) => async (dispatch, getState) => {
  try {
    debugLog('[jumpSystem] Starting jump to server:', server.sName, `(${server.bSerial})`);
    debugLog('[jumpSystem] Server data:', { sIP: server.sIP, sLocalIP: server.sLocalIP, bPort: server.bPort, fLocal: server.fLocal });

    // Dispatch jump start with the target server name so the live grid
    // overlay can show "Switching server: <name>..." instead of just
    // "Switching server...". Cleared by JUMP_SYSTEM / JUMP_SYSTEM_FAILED.
    dispatch({ type: JUMP_SYSTEM_START, payload: { targetName: server.sName || '' } });

    const state = getState();
    const { sSess, username, password } = state.auth;

    // IMPORTANT: Capture the old server URL BEFORE any state changes
    // This ensures we can properly logout from the old server even if getApiUrl() changes
    const oldServerUrl = getApiUrl();
    debugLog('[jumpSystem] Old server URL captured:', oldServerUrl);

    // Validate credentials
    if (!username || !password) {
      console.error('[jumpSystem] No credentials available for auto-login');
      dispatch({ type: JUMP_SYSTEM_FAILED });
      dispatch({ type: SET_JUMP_ERROR, payload: 'Cannot jump: No credentials available. Please log in again.' });
      return;
    }

    // Use whichIP to determine the best connection (mirrors Java AuthHelper.whichIP).
    // Note: whichIP already validates reachability via info.getSerial + serial match
    // against each candidate URL, so we don't fire a separate info.isAlive check
    // here — that was duplicating ~50-500ms of round-trip latency. If whichIP
    // exhausted all candidates without success it still returns the public IP as
    // a best-effort URL; the login below catches the failure if it really is dead.
    const connection = await whichIP(server);
    const targetUrl = connection.url;
    const targetProxyUrl = devProxyUrl(targetUrl);
    debugLog('[jumpSystem] Selected server URL:', targetUrl);

    // Step 2: Logout from OLD server — fire and forget. The session-table
    // row gets cleaned up best-effort; if it doesn't, the old NVR ages it
    // out via the inactivity timeout. Awaiting this was adding 0.5-3s to
    // every jump for no functional gain (the new login below doesn't depend
    // on the old session being terminated). ClickUp 86baduzfv.
    if (sSess && oldServerUrl) {
      const wasEviewMode = localStorage.getItem('eviewMode') === 'true';
      const logoutMethod = wasEviewMode ? 'auth.logoutUserEnterprise' : 'auth.logoutUser';
      debugLog(`[jumpSystem] Firing best-effort logout on old server (${logoutMethod}):`, oldServerUrl);
      axios.post(devProxyUrl(oldServerUrl), {
        jsonrpc: 2.0,
        method: logoutMethod,
        id: 99,
        params: [sSess]
      }, { timeout: 3000 }).catch(err => {
        debugWarn('[jumpSystem] Old server logout failed (non-critical):', err.message);
      });
      // Clear old session from localStorage immediately
      localStorage.removeItem('sSess');
    }

    // Step 3: Login to target server
    // Backend signature: loginUser(sName, sPass, fForce=False, fRemote=False)
    // fForce=true kicks out existing session (handles "exists" error on jump)
    // fRemote=true indicates this is a remote login (not local/on-NVR)
    debugLog('[jumpSystem] Attempting login to target server as:', username);
    let newSession;
    try {
      const loginResp = await axios.post(targetProxyUrl, {
        jsonrpc: 2.0,
        method: 'auth.loginUser',
        id: 2,
        params: [username, password, true, true]
      }, { timeout: 6000 });

      const result = loginResp.data?.result?.[1];
      if (!result || result.length <= 8 || result === 'maxsession' || result === 'noauth' || result === 'noremote') {
        throw new Error(result || 'Login failed');
      }
      newSession = result;
      debugLog('[jumpSystem] Login successful, session:', newSession.substring(0, 8) + '...');
    } catch (err) {
      console.error('[jumpSystem] Login to target server failed:', err.message);
      dispatch({ type: JUMP_SYSTEM_FAILED });
      dispatch({ type: SET_JUMP_ERROR, payload: `Login to ${server.sName} failed: ${err.message || 'Unknown error'}` });
      return;
    }

    // Step 4: Fetch post-login data from target server (parallel requests)
    debugLog('[jumpSystem] Fetching post-login data from target server...');
    let targetServerEview = false; // Track if target server has eView
    let targetAuthServers = []; // Track auth servers for eView camera fetching
    let eviewCamerasPromise = null; // Holds in-flight cross-NVR fetch when eview
    try {
      const [systemResp, camerasResp, ptzResp, ptzPresetsResp, serversResp, userResp, startupResp] = await Promise.all([
        axios.post(targetProxyUrl, { jsonrpc: 2.0, method: 'config.system.getAll', id: 10, params: [newSession] }, { timeout: 6000 }),
        axios.post(targetProxyUrl, { jsonrpc: 2.0, method: 'config.camera.getAllCameras', id: 11, params: [newSession] }, { timeout: 6000 }),
        axios.post(targetProxyUrl, { jsonrpc: 2.0, method: 'config.ptz.getAllConfigs', id: 12, params: [newSession] }, { timeout: 6000 }),
        axios.post(targetProxyUrl, { jsonrpc: 2.0, method: 'config.ptz.getAllPresets', id: 13, params: [newSession] }, { timeout: 6000 }),
        axios.post(targetProxyUrl, { jsonrpc: 2.0, method: 'config.server.getAllAuthServers', id: 14, params: [newSession, username] }, { timeout: 6000 }),
        axios.post(targetProxyUrl, { jsonrpc: 2.0, method: 'config.user.getUserByName', id: 15, params: [newSession, username] }, { timeout: 6000 }),
        axios.post(targetProxyUrl, { jsonrpc: 2.0, method: 'config.meta.getStartup', id: 18 }, { timeout: 6000 })
      ]);

      // Dispatch all the data
      if (systemResp.data?.result?.[1]) {
        dispatch({ type: SET_AUTOSCAN_TIMEOUT, payload: systemResp.data.result[1].bAutoScanTimeout || 10 });
      }
      if (camerasResp.data?.result?.[1]) {
        dispatch({ type: SET_ALL_CAMERAS, payload: camerasResp.data.result[1] });
      }
      if (ptzResp.data?.result?.[1]) {
        dispatch({ type: SET_PTZ_CONFIG, payload: ptzResp.data.result[1] });
      }
      if (ptzPresetsResp.data?.result?.[1]) {
        dispatch({ type: SET_PTZ_PRESETS, payload: ptzPresetsResp.data.result[1] });
      }
      if (serversResp.data?.result?.[1]) {
        const servers = serversResp.data.result[1];
        const sortedByName = servers.sort((a, b) => (a.sName > b.sName) ? 1 : 0);
        dispatch({ type: SET_AUTH_SERVERS, payload: sortedByName, sApiToken: servers[0]?.sApiToken });
      }

      // Dispatch GET_SERVER with the new server's info (critical for bSerial)
      if (startupResp.data?.result?.[1]) {
        const data = startupResp.data.result[1];
        const general = data[Object.keys(data)[0]];
        const authServers = data[Object.keys(data)[1]] || [];
        const usersOnline = data[Object.keys(data)[2]] || [];
        const eview = general?.rgsFeature?.includes('eview') || false;
        const sApiToken = authServers?.[0]?.sApiToken || '';

        // Store for later use (views fetch, eView camera loading)
        targetServerEview = eview;
        targetAuthServers = authServers;

        debugLog('[jumpSystem] New server info - bSerial:', general?.bSerial, 'sName:', general?.sName, 'fEview:', eview);

        dispatch({
          type: GET_SERVER,
          bSerial: general.bSerial,
          sName: general.sName,
          sVersion: general.sVersion || '',
          sReleaseVersion: general.sReleaseVersion || '',
          fReleaseCandidate: !!general.fReleaseCandidate,
          bNumcams: general.bNumcams || 0,
          sApiToken: sApiToken,
          authServers: authServers,
          usersOnline: usersOnline,
          fEview: eview
        });

        // Set useEviewLogin based on target server's fEview flag
        // If server has eView → eView mode, if not → dview mode
        dispatch({ type: SET_EVIEW_LOGIN, payload: eview });
        debugLog('[jumpSystem] Set useEviewLogin to:', eview);

        // Update localStorage to match
        if (eview) {
          localStorage.setItem('eviewMode', 'true');
        } else {
          localStorage.removeItem('eviewMode');
        }
      }

      // Kick off the cross-NVR cameras fetch in parallel with the user
      // rights/cameras chain below. Both are reachable now (we have
      // targetAuthServers from startupResp + newSession from login) and
      // neither depends on the other. Awaited at the end of this try
      // block — Step 6a below is now just `await eviewCamerasPromise`.
      if (targetServerEview && targetAuthServers && targetAuthServers.length > 0) {
        debugLog('[jumpSystem] eView: kicking off cross-NVR cameras fetch (parallel with user rights)');
        eviewCamerasPromise = fetchAllServerCamerasForJump(
          dispatch, newSession, username, password, targetAuthServers, server.bSerial, targetUrl
        ).catch(e => {
          debugWarn('[jumpSystem] Cross-NVR cameras fetch failed:', e);
          return [];
        });
      }

      if (userResp.data?.result?.[1]) {
        const userData = userResp.data.result[1];
        dispatch({
          type: SET_USER_DATA,
          payload: userData.bID,
          bID: userData.bID,
          bInactivityTimeout: userData.bInactivityTimeout,
          bType: userData.bType,
          sDescription: userData.sDescription,
          sName: userData.sName,
          sPassword: password
        });

        // Get user rights and cameras
        const [rightsResp, userCamsResp] = await Promise.all([
          axios.post(targetProxyUrl, { jsonrpc: 2.0, method: 'config.user.getRights', id: 16, params: [newSession, userData.bID, server.bSerial] }, { timeout: 6000 }),
          axios.post(targetProxyUrl, { jsonrpc: 2.0, method: 'config.user.getCameras', id: 17, params: [newSession, userData.bID, server.bSerial] }, { timeout: 6000 })
        ]);

        // Select THIS server's rights/cameras row by serial (see util/serverRights):
        // getRights/getCameras return one [serial, ...] row per server.
        const rightsValues = selectServerValues(rightsResp.data?.result?.[1], server.bSerial);
        if (rightsValues.length) {
          let fAccess = 0, fAdmin = 0, fExport = 0, fRemote = 0, fPtz = 0, fPlayback = 0;
          rightsValues.forEach(r => {
            if (r === 1) fAccess = 1;
            if (r === 2) fAdmin = 1;
            if (r === 4) fExport = 1;
            if (r === 8) fRemote = 1;
            if (r === 16) fPtz = 1;
            if (r === 32) fPlayback = 1;
          });
          dispatch({ type: SET_USER_RIGHTS, fAccess, fAdmin, fExport, fRemote, fPtz, fPlayback });
        }

        const jumpCams = selectServerValues(userCamsResp.data?.result?.[1], server.bSerial);
        if (jumpCams.length) {
          dispatch({ type: SET_USER_CAMERAS, payload: jumpCams });
        }
      }

    } catch (err) {
      console.error('[jumpSystem] Failed to fetch post-login data:', err.message);
      // Continue anyway - we're logged in, just missing some data
    }

    // Step 5: Clear cached stream preferences and update state
    // Note: Logout from old server already done in Step 2 (before login)
    clearStreamPrefs();
    localStorage.setItem('sSess', newSession);

    // Set jumped server URL so subsequent API calls go to the new server
    setJumpedServerUrl(targetUrl);

    // Step 6: Clear old views and prepare for new views from target server
    debugLog('[jumpSystem] Clearing old views and loading views from new server...');
    dispatch({ type: SET_VIEWS, payload: [] }); // Clear view list
    dispatch({ type: SET_CURRENT_VIEW, payload: '' }); // Clear current view selection
    dispatch({ type: RESTORE_ORIGINAL_CAMERAS }); // Reset camera array state

    const jumpedSerial = server.bSerial;
    debugLog('[jumpSystem] Fetching views for server bSerial:', jumpedSerial, 'eView mode:', targetServerEview);

    // Step 6a: Wait for the cross-NVR cameras fetch that was started in parallel
    // with user rights/cameras above. Smart Views generation in getViewsAction
    // below requires allCameras to be populated for multi-NVR view materialization.
    if (eviewCamerasPromise) {
      debugLog('[jumpSystem] Awaiting in-flight cross-NVR cameras fetch...');
      await eviewCamerasPromise;
      debugLog('[jumpSystem] Cross-NVR cameras fetch complete');
    }

    // Step 6b: Load views using ViewActions.getViews which includes Smart Views generation
    // This uses the proper view loading flow with auto-generated default views and Smart Views
    try {
      const bUserID = state.user.bID || 0;
      debugLog('[jumpSystem] Loading views via ViewActions.getViews...');
      await dispatch(getViewsAction(newSession, bUserID, targetServerEview));
      debugLog('[jumpSystem] Views loaded successfully');
    } catch (err) {
      debugWarn('[jumpSystem] Failed to load views via ViewActions:', err.message);
    }

    // Dispatch jump system state update
    dispatch({
      type: JUMP_SYSTEM,
      payload: {
        server,
        serverUrl: targetUrl
      }
    });

    // Update login state
    dispatch({ type: LOGIN_SUCCESS, payload: newSession });
    dispatch({ type: LOGIN_RESULT, payload: '' });

    // Defensive realign: ensure currentCamView matches conf BEFORE any
    // re-render happens. autoSelectDefaultView below normally aligns this
    // (via selectView → CAM_VIEW_CHANGED), but if it bails early (no
    // matching view, race with data load) the previous session's
    // currentCamView stays in state alongside the preserved conf — user
    // sees the conf-N grid with only 1 cam rendered + N-1 black cells.
    // REHYDRATE handler in CamButtonsReducer covers app-load; this covers
    // mid-session jump-back.
    const currentConf = getState().config && getState().config.conf;
    if (currentConf) {
      dispatch({ type: CAM_VIEW_CHANGED, payload: getCamViewForConf(currentConf) });
    }

    // Auto-select the appropriate view using ViewActions
    // This uses the proper priority: localStorage saved view > default view > first view
    debugLog('[jumpSystem] Auto-selecting default view...');
    dispatch(autoSelectDefaultView());

    debugLog('[jumpSystem] Jump complete to:', server.sName, '- eView:', targetServerEview);

    // Navigate to live view (in case we're somewhere else)
    history.push('/live');

  } catch (error) {
    console.error('[jumpSystem] Jump failed with unexpected error:', error);
    dispatch({ type: JUMP_SYSTEM_FAILED });
    dispatch({ type: SET_JUMP_ERROR, payload: `Jump failed: ${error.message}. Please try again.` });
  }
};

/**
 * Set Active Server - For eView mode to track which server is currently selected
 * @param {Object} server - The server object to set as active
 */
export const setActiveServer = (server) => {
  return {
    type: SET_ACTIVE_SERVER,
    payload: server
  };
};